import React, { useContext, useEffect, useState } from "react";
import { TextField } from "@mui/material"
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  makeStyles,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import { ErrorMessage, FastField, Field, Form, Formik } from "formik";

import { OfferedAnswer, OptionalQuestion } from "./types";
import {
  getSurveyQuestions,
  postSurvey,
  saveSurvey,
} from "../../api/hospital";
import {
  ABORTION_CHECKBOX,
  BIRTH_CHECKBOX,
  checkInputType,
  createAnswersForRadios,
  isCheckboxInput, isOptionalQuestion,
  isRequired,
  markCheckboxes,
  MPO_CHECKBOX,
  offeredAnswersQuestions,
  SERVICE_SELECTION_CATEGORY,
  SERVICE_SELECTION_QUESTION,
  surveyCategoryFilter,
  surveyQuestionFilter,
  validateBlank,
} from "../../util/form";
import { HospitalSurvey, SurveyAnswer } from "../../types/hospital";

import InputLabel from "./InputLabel";
import FormLabel from "./FormLabel";
import { invalidSurveyForm, requiredField } from "../../constants/messages";
import DialogActions from "./DialogActions";
import { blankOfferedAnswers, blankSurvey } from "../../constants/objects";
import { UserContext } from "../../Routes";

const useStyles = makeStyles((theme) => ({
  error: {
    color: theme.palette.error.main,
  },
  focused: {
    borderRadius: 4,
    "& .MuiFilledInput-root": {
      padding: "12px 10px 10px 12px",
      borderRadius: 4,
      "&.Mui-focused": {
        border: `1px solid ${theme.palette.secondary.main}`,
        backgroundColor: theme.palette.secondary.light,
      },
    },
  },
  required: {
    color: "#992468",
    fontSize: 24,
  },
  marginNone: {
    margin: 0,
  },
  hr: {
    height: 1,
    backgroundColor: theme.palette.grey[300],
    border: "none",
    margin: theme.spacing(4, 0),
  },
}));

type HospitalSurveyFormProps = {
  handleClose: () => void;
  surveyData: HospitalSurvey;
  handleDataChange: (surveyData: HospitalSurvey) => void;
  hospitalId?: number;
  removeData?: () => void;
  optionalQuestions: OptionalQuestion[];
  setApproveModalOpen?: React.Dispatch<React.SetStateAction<boolean>>
};

export default function HospitalSurveyForm({ handleClose, surveyData, handleDataChange, hospitalId, removeData, optionalQuestions, setApproveModalOpen }: HospitalSurveyFormProps) {
  const classes = useStyles();
  const [checkboxFields, setCheckboxFields] =
    useState<Map<number, OfferedAnswer[]>>(blankOfferedAnswers);
  const [radioOfferedQuestionsFields, setRadioOfferedQuestionsFields] =
    useState<Map<number, OfferedAnswer[]>>(blankOfferedAnswers);

  let fieldName: string;

  const { user } = useContext(UserContext);

  useEffect(() => {
    async function createForm(data: HospitalSurvey) {
      handleDataChange(data);
      const responseCheckboxes = await markCheckboxes(data);
      const radioOfferedAnswers = await createAnswersForRadios();
      setCheckboxFields(responseCheckboxes);
      setRadioOfferedQuestionsFields(radioOfferedAnswers);
    }

    async function fetchData() {
      const response = await getSurveyQuestions();
      createForm({
        ...blankSurvey,
        answers: response.data.questionsAndAnswers,
      });
    }

    if (!surveyData.answers.length) {
      fetchData();
    } else {
      createForm(surveyData);
    }
  }, []);

  function closeModal(close: () => void) {
    close();
  }

  // Concatenates all marked/selected checkboxes to make a single string.
  // This string will be inserted into database as the survey answer for that question.
  function makeFinalAnswer(surveyAnswer: SurveyAnswer): SurveyAnswer {
    const answers = checkboxFields!
      .get(surveyAnswer.questionId)!
      .filter((answer) => answer.selected)
      .map((answer) => answer.offeredAnswerText.toLowerCase());

    let answerString = answers.join(", ");
    answerString = answerString.charAt(0).toUpperCase() + answerString.slice(1);

    return {
      ...surveyAnswer,
      answerText: answerString,
    };
  }

  function handleSubmitForm(
    values: HospitalSurvey,
    setValues: (
      values: React.SetStateAction<HospitalSurvey>,
      shouldValidate?: boolean | undefined
    ) => void,
    handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void
  ) {
    let tmp = { ...values };
    tmp.status = null;
    tmp.lastUpdate = "";
    for (const [i, questionCategory] of tmp.answers.entries()) {
      for (const [
        j,
        surveyAnswer,
      ] of questionCategory.surveyAnswers.entries()) {
        if (isCheckboxInput(surveyAnswer)) {
          tmp.answers[i].surveyAnswers[j] = makeFinalAnswer(surveyAnswer);
        }
      }
    }
    setValues(tmp);
    handleSubmit();
  }

  function getCategoryFilter(categoryId: number) {
    return surveyCategoryFilter(categoryId, checkboxFields.get(SERVICE_SELECTION_QUESTION)![BIRTH_CHECKBOX].selected, checkboxFields.get(SERVICE_SELECTION_QUESTION)![ABORTION_CHECKBOX].selected, checkboxFields.get(SERVICE_SELECTION_QUESTION)![MPO_CHECKBOX].selected);
  }

  function getQuestionFilter(surveyAnswers: SurveyAnswer[], surveyAnswer: SurveyAnswer){
    return isOptionalQuestion(surveyAnswer.questionId) && surveyQuestionFilter(optionalQuestions, surveyAnswer.questionId, surveyAnswers)
  }

  return checkboxFields.size === offeredAnswersQuestions.size ? (
    <>
      <Formik
        enableReinitialize
        initialValues={surveyData}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={async (values) => {
          if (user?.authority === "NURSE") {
            const response = await postSurvey(values);
            handleDataChange(response.data);
          } else {
            const response = await saveSurvey(values, hospitalId!);
            handleDataChange(response.data);
            if (setApproveModalOpen) {
              setApproveModalOpen(true);
            }
          }
          closeModal(handleClose);
        }}
      >
        {({ values, setFieldValue, setValues, handleSubmit, isValid }) => (
          <Form>
            <Typography color="textSecondary" style={{ marginBottom: -12 }}>
              (<span className={classes.required}> * </span>) -{" "}
              {requiredField.toLowerCase()}
            </Typography>
            {values.answers.map((category, idxCategory) => (
              <React.Fragment key={category.category}>
                <div
                  hidden={!getCategoryFilter(idxCategory)}>
                  <FormLabel>
                    <hr className={classes.hr}/>
                    {category.category}
                  </FormLabel>
                  {category.surveyAnswers.map((surveyAnswer, idxQuestion) => (
                    <div key={surveyAnswer.questionId - 1}
                         hidden={getQuestionFilter(category.surveyAnswers, surveyAnswer)}>
                      {
                        <div style={{ display: "none" }}>
                          {
                            (fieldName = `answers[${idxCategory}].surveyAnswers[${idxQuestion}].answerText`)
                          }
                        </div>
                      }
                      <InputLabel>
                        {surveyAnswer.questionText}
                        {isRequired(surveyAnswer) && (
                          <span className={classes.required}>*</span>
                        )}
                      </InputLabel>
                      <Field
                        value={
                          isCheckboxInput(surveyAnswer)
                            ? checkboxFields.get(surveyAnswer.questionId)![
                            checkboxFields.get(surveyAnswer.questionId)!
                              .length - 1
                              ].offeredAnswerText
                            : surveyAnswer.answerText
                        }
                        name={fieldName}
                        questionid={surveyAnswer.questionId}
                        checkboxfields={checkboxFields}
                        radioAnswers={radioOfferedQuestionsFields}
                        setfieldvalue={setFieldValue}
                        setcheckboxfields={setCheckboxFields}
                        validate={(getCategoryFilter(idxCategory) && !getQuestionFilter(category.surveyAnswers, surveyAnswer)) && (isRequired(surveyAnswer) && validateBlank)}
                        hideText={idxCategory === SERVICE_SELECTION_CATEGORY}
                        as={checkInputType(surveyAnswer)}
                      />
                      <ErrorMessage
                        name={fieldName}
                        component={FormHelperText}
                        className={classes.error}
                      />
                    </div>
                  ))}
                </div>
              </React.Fragment>
            ))}
            <DialogActions
              handleClose={handleClose}
              handleSubmit={() =>
                handleSubmitForm(values, setValues, handleSubmit)
              }
              message={!isValid && invalidSurveyForm}
            />
          </Form>
        )}
      </Formik>
    </>
  ) : (
    <></>
  );
}

export function FormikRadioGroup(props: any) {
  return (
    <RadioGroup {...props}>
      <FormControlLabel key="Da" value="Da" control={<Radio/>} label="Da"/>
      <FormControlLabel key="Ne" value="Ne" control={<Radio/>} label="Ne"/>
    </RadioGroup>
  );
}

export function FormikOfferedQuestionsRadioGroup(props: any) {
  return (
    <RadioGroup {...props}>
      {props.radioAnswers.get(props.questionid)?.map((offeredAnswer: OfferedAnswer, index: number) => {
        return (
          <FormControlLabel key={index} value={offeredAnswer.offeredAnswerText} control={<Radio/>}
                            label={offeredAnswer.offeredAnswerText}/>
        )
      })}
    </RadioGroup>
  );
}

export function FormikFastField(props: any) {
  const classes = useStyles();

  return (
    <FastField
      as={TextField}
      {...props}
      fullWidth
      variant="filled"
      multiline
      InputProps={{ disableUnderline: true }}
      className={classes.focused}
    />
  );
}

export function FormikCheckboxGroup({ name, value, questionid, checkboxfields, setfieldvalue, setcheckboxfields, hideText }: {
  name: string;
  value: string;
  questionid: number;
  setfieldvalue: (field: string, value: any, shouldValidate?: boolean) => void;
  checkboxfields: Map<number, OfferedAnswer[]>;
  setcheckboxfields: (
    checkboxfields: Map<number, OfferedAnswer[]>
  ) => React.Dispatch<React.SetStateAction<Map<number, OfferedAnswer[]>>>;
  hideText: boolean;
}) {
  const classes = useStyles();
  const [textFieldValue, setTextFieldValue] = useState<string>(value || "");

  useEffect(() => {
    handleTextChange(value);
  }, [value]);

  useEffect(() => {
    updateCheckboxes(checkboxfields.get(questionid)!.length - 1, true);
  }, [textFieldValue]);

  // Handles checking/unchecking of checkboxes (updates state of parent component)
  function updateCheckboxes(index: number, isTextField?: boolean) {
    let currentQuestionCheckboxesCopy = JSON.parse(
      JSON.stringify(checkboxfields.get(questionid))
    ) as OfferedAnswer[];

    if (isTextField) {
      currentQuestionCheckboxesCopy[index].offeredAnswerText = textFieldValue;
      currentQuestionCheckboxesCopy[index].selected = textFieldValue
        ? true
        : false;
    } else {
      currentQuestionCheckboxesCopy[index].selected =
        !checkboxfields.get(questionid)![index].selected;
    }

    let checkboxesCopy = new Map(checkboxfields);
    checkboxesCopy.set(questionid, currentQuestionCheckboxesCopy);

    setcheckboxfields(checkboxesCopy);
  }

  function handleCheckboxChange(index: number) {
    updateCheckboxes(index, false);
  }

  function handleTextChange(text: string) {
    setTextFieldValue(text);
    setfieldvalue(name, textFieldValue, false);
  }

  return (
    <FormControl component="fieldset" fullWidth>
      <FormGroup>
        {checkboxfields.get(questionid)?.filter((offeredAnswer, index) => !(hideText && index === checkboxfields.get(questionid)!.length - 1)).map((offeredAnswer, index) => {
          return (
            <FormControlLabel
              control={
                <Checkbox
                  checked={offeredAnswer.selected}
                  onChange={() => {
                    handleCheckboxChange(index);
                  }}
                  name={name}
                  value={offeredAnswer.offeredAnswerText}
                />
              }
              label={
                index === checkboxfields.get(questionid)!.length - 1
                  ? "Nešto drugo:"
                  : offeredAnswer.offeredAnswerText
              }
              key={questionid + index}
            />
          );
        })}
        <TextField
          name={name}
          value={textFieldValue}
          fullWidth
          variant="filled"
          multiline
          InputProps={{ disableUnderline: true }}
          className={classes.focused}
          style={hideText ? { display: "none" } : {}}
          onChange={(event) => handleTextChange(event.target.value)}
        />
      </FormGroup>
    </FormControl>
  );
}
